
import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaMobile, FaTablet, FaHeadphones, FaLaptop, FaSave, FaCheck, FaEye, FaEyeSlash, FaSearch } from 'react-icons/fa';
import { categoryService } from '../../services/categoryService';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({ 
    name: '', 
    icon: '', 
    description: '',
    order: 0,
    isActive: true 
  });

  const categoryIcons = [
    '📱', '📲', '💻', '🖥️', '⌚', '🎧', '🔊', '📷', '🎮', '🔋', 
    '💿', '📀', '🖨️', '📡', '🔌', '💻', '🖱️', '📹', '🎥', '📻'
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = categories.filter(cat => 
        cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (cat.description && cat.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredCategories(filtered);
    } else {
      setFilteredCategories(categories);
    }
  }, [searchTerm, categories]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await categoryService.getCategories();
      setCategories(response.data);
      setFilteredCategories(response.data);
    } catch (error) {
      toast.error('Failed to fetch categories');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter category name');
      return;
    }

    const loadingToast = toast.loading(editingCategory ? 'Updating category...' : 'Creating category...');
    
    try {
      if (editingCategory) {
        await categoryService.updateCategory(editingCategory._id, formData);
        toast.success('Category updated successfully!', { id: loadingToast });
      } else {
        await categoryService.createCategory(formData);
        toast.success('Category created successfully!', { id: loadingToast });
      }
      setShowModal(false);
      setEditingCategory(null);
      setFormData({ name: '', icon: '', description: '', order: 0, isActive: true });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save category', { id: loadingToast });
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({ 
      name: category.name, 
      icon: category.icon || '📱', 
      description: category.description || '',
      order: category.order || 0,
      isActive: category.isActive !== false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Are you sure you want to delete this category? Products in this category will be affected.');
    if (!confirmed) return;

    const loadingToast = toast.loading('Deleting category...');
    
    try {
      await categoryService.deleteCategory(id);
      toast.success('Category deleted successfully', { id: loadingToast });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete category', { id: loadingToast });
    }
  };

  const handleToggleStatus = async (category) => {
    const loadingToast = toast.loading('Updating status...');
    try {
      await categoryService.updateCategory(category._id, { isActive: !category.isActive });
      toast.success(`Category ${!category.isActive ? 'activated' : 'deactivated'}`, { id: loadingToast });
      fetchCategories();
    } catch (error) {
      toast.error('Failed to update status', { id: loadingToast });
    }
  };

  const getIconColor = (icon) => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
    const index = (icon?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl p-6 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2">Category Management</h1>
            <p className="text-primary-100">Manage your product categories and organization</p>
          </div>
          <button
            onClick={() => {
              setEditingCategory(null);
              setFormData({ name: '', icon: '📱', description: '', order: 0, isActive: true });
              setShowModal(true);
            }}
            className="bg-white text-primary-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-all flex items-center gap-2 shadow-lg"
          >
            <FaPlus /> Add New Category
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-4">
        <div className="relative">
          <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search categories by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-900"
          />
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-4 text-white">
          <p className="text-sm opacity-90">Total Categories</p>
          <p className="text-3xl font-bold">{categories.length}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-4 text-white">
          <p className="text-sm opacity-90">Active Categories</p>
          <p className="text-3xl font-bold">{categories.filter(c => c.isActive).length}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-4 text-white">
          <p className="text-sm opacity-90">Categories with Products</p>
          <p className="text-3xl font-bold">{categories.filter(c => c.productCount > 0).length}</p>
        </div>
      </div>

      {/* Categories Grid */}
      {filteredCategories.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-12 text-center">
          <div className="text-6xl mb-4">📂</div>
          <h3 className="text-xl font-semibold mb-2">No categories found</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {searchTerm ? 'Try a different search term' : 'Get started by creating your first category'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => {
                setEditingCategory(null);
                setFormData({ name: '', icon: '📱', description: '', order: 0, isActive: true });
                setShowModal(true);
              }}
              className="btn-primary inline-flex items-center gap-2"
            >
              <FaPlus /> Create Category
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredCategories.map((category, index) => (
              <motion.div
                key={category._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft hover:shadow-hard transition-all duration-300 overflow-hidden group"
              >
                {/* Category Header */}
                <div className="relative h-32 bg-gradient-to-br overflow-hidden">
                  <div 
                    className="absolute inset-0 bg-gradient-to-br opacity-90"
                    style={{ background: `linear-gradient(135deg, ${getIconColor(category.icon)}20, ${getIconColor(category.icon)}40)` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-7xl filter drop-shadow-lg">{category.icon || '📱'}</span>
                  </div>
                  <div className="absolute top-3 right-3 flex gap-2">
                    <button
                      onClick={() => handleToggleStatus(category)}
                      className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-gray-600 hover:bg-white transition-colors"
                      title={category.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {category.isActive ? <FaEye size={14} /> : <FaEyeSlash size={14} />}
                    </button>
                    <button
                      onClick={() => handleEdit(category)}
                      className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-blue-600 hover:bg-white transition-colors"
                      title="Edit category"
                    >
                      <FaEdit size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(category._id)}
                      className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-red-600 hover:bg-white transition-colors"
                      title="Delete category"
                    >
                      <FaTrash size={14} />
                    </button>
                  </div>
                </div>

                {/* Category Content */}
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-xl text-gray-800 dark:text-white mb-1">
                        {category.name}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Slug: /{category.slug}
                        </span>
                        {category.order > 0 && (
                          <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                            Order: {category.order}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      category.isActive 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {category.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>

                  {category.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                      {category.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Products:</span>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">
                        {category.productCount || 0}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Parent:</span>
                      <span className="text-sm">
                        {category.parentCategory?.name || '—'}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center z-50 p-4 overflow-auto"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-xl sm:max-w-2xl max-h-[calc(100vh-6rem)] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-primary-600 to-primary-800 px-6 py-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-white">
                    {editingCategory ? '✏️ Edit Category' : '➕ Add New Category'}
                  </h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    <FaTimes size={20} />
                  </button>
                </div>
                <p className="text-primary-100 text-sm mt-1">
                  {editingCategory ? 'Update category information' : 'Fill in the details to create a new category'}
                </p>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleSubmit} className="p-6 overflow-auto max-h-[calc(100vh-8rem)]">
                <div className="space-y-5">
                  {/* Category Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Smartphones, Accessories, Tablets"
                      className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-900"
                      required
                    />
                  </div>

                  {/* Icon Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category Icon
                    </label>
                    <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mb-3">
                      {categoryIcons.map((icon) => (
                        <button
                          key={icon}
                          type="button"
                          onClick={() => setFormData({ ...formData, icon })}
                          className={`text-2xl p-2 rounded-lg transition-all ${
                            formData.icon === icon
                              ? 'bg-primary-100 ring-2 ring-primary-500'
                              : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      placeholder="Or enter custom emoji (e.g., 📱)"
                      className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Brief description of this category"
                      rows="3"
                      className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-900 resize-none"
                    />
                  </div>

                  {/* Order & Status */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Display Order
                      </label>
                      <input
                        type="number"
                        value={formData.order}
                        onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                        className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary-500 bg-gray-50 dark:bg-gray-900"
                      />
                      <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Status
                      </label>
                      <div className="flex gap-4 pt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.isActive === true}
                            onChange={() => setFormData({ ...formData, isActive: true })}
                            className="w-4 h-4 text-primary-600"
                          />
                          <span className="text-sm">Active</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.isActive === false}
                            onChange={() => setFormData({ ...formData, isActive: false })}
                            className="w-4 h-4 text-primary-600"
                          />
                          <span className="text-sm">Inactive</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex flex-col sm:flex-row gap-4 mt-8">
                  <button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 transition-all flex items-center justify-center gap-2"
                  >
                    <FaSave /> {editingCategory ? 'Update Category' : 'Create Category'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingCategory(null);
                      setFormData({ name: '', icon: '📱', description: '', order: 0, isActive: true });
                    }}
                    className="flex-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 py-3 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CategoryManagement;